<template>
    <div class="notes-bubble-card">
        <div class="notes-sidebar-header">
            <div class="sidebar-title">{{ $t("notes.title") }}</div>
            <div class="bubble-header-actions">
                <button class="bubble-icon-button" @click="emit('close')">
                    <img src="@/assets/images/svg/double_arrow_left.svg" :alt="$t('Common.close')">
                </button>
                <button class="bubble-icon-button">
                    <img src="@/assets/images/svg/import_outline.svg" :alt="$t('Common.download')">
                </button>
            </div>
        </div>
        <div class="note-categories">
            <button :class="{ active: selectedCategory === 'all' }" @click="selectCategory('all')">
                <span class="category-left"><span class="category-icon"><img :src="noteIcon" alt=""></span>{{ $t("notes.all_notes") }}</span>
                <span>{{ noteCounts.all }}</span>
            </button>
            <button :class="{ active: selectedCategory === 'mine' }" @click="selectCategory('mine')">
                <span class="category-left"><span class="category-icon"><img :src="userIcon" alt=""></span>{{ $t("notes.my_notes_filter") }}</span>
                <span>{{ noteCounts.mine }}</span>
            </button>
            <button :class="{ active: selectedCategory === 'shared' }" @click="selectCategory('shared')">
                <span class="category-left"><span class="category-icon"><img :src="shareIcon" alt=""></span>{{ $t("notes.shared_with_me_filter") }}</span>
                <span>{{ noteCounts.shared }}</span>
            </button>
        </div>
        <div class="sidebar-panel">
            <div class="panel-title">{{ $t("notes.pinned_notes") }}</div>
            <div v-if="pinnedNotes.length" class="pinned-list">
                <button v-for="note in pinnedNotes" :key="note._id" class="note-item" @click="openNote(note._id)">
                    <span class="pinned-file-icon"><img :src="pinnedIcon" alt=""></span>
                    <span>{{ note.title || $t("notes.untitled") }}</span>
                </button>
            </div>
            <div v-else class="panel-empty panel-empty--compact">
                <span class="pin-empty-icon"><img :src="pinIcon" alt=""></span>
                <p>{{ $t("notes.pin_note_empty") }}</p>
            </div>
        </div>
        <div class="sidebar-panel sidebar-panel--scroll">
            <div class="panel-title">{{ $t("notes.recent") }}</div>
            <ul class="recent-list">
                <li v-for="note in recentNotes" :key="note._id" @click="openNote(note._id)" :class="{ selected: note._id === activeNoteId }">
                    <span class="recent-file-icon"><img :src="recentIcon" alt=""></span>
                    <span class="recent-note-title">{{ note.title || $t("notes.untitled") }}</span>
                </li>
            </ul>
            <div v-if="!recentNotes.length" class="recent-empty">{{ $t("notes.no_recent_notes") }}</div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from "vuex";
import { apiRequest } from '@/services'

const { getters } = useStore();
const noteIcon = require("@/assets/images/svg/file_smile.svg")
const userIcon = require("@/assets/images/svg/User_icon.svg")
const shareIcon = require("@/assets/images/svg/share_outline.svg")
const recentIcon = require("@/assets/images/svg/file_gray.svg")
const pinnedIcon = require("@/assets/images/svg/file_gray.svg")
const pinIcon = require("@/assets/images/menu/pin.svg")

const emit = defineEmits(['close'])

const route = useRoute()
const router = useRouter()
const currentUser = computed(() => getters["users/currentUser"])
const notes = ref([])
const selectedCategory = ref('all')
const activeNoteId = computed(() => route.query.nid)
const currentUserId = computed(() => currentUser.value?._id || currentUser.value?.id || currentUser.value?.userId || "")

function getNoteOwnerId(note) {
    return note?.userId || note?.createdBy || note?.creatorId || note?.ownerId || ""
}

const noteCounts = computed(() => ({
    all: notes.value.length,
    mine: notes.value.filter(note => String(getNoteOwnerId(note)) === String(currentUserId.value)).length,
    shared: notes.value.filter(note => String(getNoteOwnerId(note)) !== String(currentUserId.value)).length,
}))
const pinnedNotes = computed(() => notes.value.filter(note => note.is_pin || note.isPin || note.isPinned))
const recentNotes = computed(() => [...notes.value].sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)).slice(0, 5))

function openNote(noteId) {
    if (!noteId) return

    router.push({
        path: `/${companyId.value}/note`,
        query: { nid: noteId },
    })
    }

function selectCategory(category) {
    selectedCategory.value = category
    const query = { ...route.query, filter: category }
    delete query.nid
    if (category === "all") delete query.filter
    router.replace({ path: `/${companyId.value}/note`, query })
}

function upsertNoteFromEvent(event) {
    const note = event?.detail?.note;
    if (!note?._id) return;

    const index = notes.value.findIndex((item) => item._id === note._id);
    if (index >= 0) {
        notes.value[index] = { ...notes.value[index], ...note };
    } else {
        notes.value.unshift(note);
    }
    notes.value = [...notes.value];
}

function removeNoteFromEvent(event) {
    const noteId = event?.detail?.noteId;
    if (!noteId) return;

    notes.value = notes.value.filter((note) => note._id !== noteId);
}

async function fetchNotes(userId) {
    if (!userId) return
    try {
        const res = await apiRequest('get', `/api/v1/notes?userId=${userId}`)
        notes.value = res?.data?.data || []
    } catch (err) {
        console.error('Fetch notes error:', err)
    }
}


watch(() => route.name, (name) => {
    if (name === 'notes') {
        if (currentUser.value?._id) fetchNotes(currentUser.value._id)
        return
    }
})

watch(() => route.query.filter, (filter) => {
    selectedCategory.value = ["mine", "shared"].includes(filter) ? filter : "all"
}, { immediate: true })

watch(() => currentUser.value?._id, (userId) => {
    if (route.name === 'notes' && userId) {
        fetchNotes(userId)
    }
})

onMounted(() => {
    window.addEventListener("note-created", upsertNoteFromEvent)
    window.addEventListener("note-pin-updated", upsertNoteFromEvent)
    window.addEventListener("note-updated", upsertNoteFromEvent)
    window.addEventListener("note-deleted", removeNoteFromEvent)
    if (route.name === 'notes' && currentUser.value?._id) {
        fetchNotes(currentUser.value._id)
    }
})

onUnmounted(() => {
    window.removeEventListener("note-created", upsertNoteFromEvent)
    window.removeEventListener("note-pin-updated", upsertNoteFromEvent)
    window.removeEventListener("note-updated", upsertNoteFromEvent)
    window.removeEventListener("note-deleted", removeNoteFromEvent)
})

const companyId = computed(() => route.params.cid || route.params.companyId || 'default')

</script>

<style scoped src="./style.css">
</style>
